import { Injectable, Pipe, PipeTransform } from '@angular/core';

@Pipe({
    name: 'currencySymbol',
    standalone: false
})
@Injectable({ providedIn: 'root' }) // TODO: Pipes should be pure, stateless and not injectable. Refactor to a service if caching or dependencies are needed.
export class CurrencySymbolPipe implements PipeTransform {
    // Simple cache to avoid recomputing for the same (code, locale)
    private cache = new Map<string, string>();

    /**
     * Transforms a currency code (e.g., 'USD') to its symbol (e.g., '$').
     * @param code ISO 4217 currency code, e.g., 'USD', 'EUR'
     * @param locale Optional BCP 47 locale, e.g., 'en-US', 'fr-FR'
     */
    transform(code: string | null | undefined, locale: string = 'en-US'): string {
        if (!code) {
            return '';
        }
        const normalized = code.toUpperCase().trim();
        const key = `${locale}|${normalized}`;
        const cached = this.cache.get(key);
        if (cached) return cached;

        // Try narrow symbol first (shorter, context-friendly)
        let symbol = this.getSymbol(normalized, locale, 'narrowSymbol');

        // Fallback to standard symbol if narrow symbol not distinct
        if (!symbol || symbol === normalized) {
            const fallbackSymbol = this.getSymbol(normalized, locale, 'symbol');
            symbol = fallbackSymbol || normalized;
        }

        this.cache.set(key, symbol);
        return symbol;
    }

    private getSymbol(
        code: string,
        locale: string | undefined,
        currencyDisplay: 'symbol' | 'narrowSymbol'
    ): string | null {
        try {
            const nf = new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: code,
                currencyDisplay,
                // We only care about the symbol; keep formatting minimal
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            });

            // Extract the currency symbol via formatToParts
            const parts = nf.formatToParts(0);
            const currencyPart = parts.find(p => p.type === 'currency');
            if (currencyPart?.value) {
                return currencyPart.value;
            }

            // Some locales may embed the currency within literal parts
            // As a secondary attempt, strip digits and spacing from the formatted string
            const formatted = nf.format(0);
            const candidate = formatted.replace(/[0-9\s.,\u00A0]/g, '').trim();
            return candidate || null;
        } catch {
            // Invalid currency code or unsupported locale
            return null;
        }
    }
}